import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, List, X } from 'lucide-react';
import { slides, slideTitles } from './slides';
import { useScreenInit } from '../useScreenInit.js';
export function Deck() {
  const screenInit = useScreenInit();
  const [currentIndex, setCurrentIndex] = useState(typeof screenInit?.slide === 'number' ? screenInit.slide : 0);
  const [direction, setDirection] = useState(0);
  const [tocOpen, setTocOpen] = useState(false);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        if (currentIndex < slides.length - 1) {
          setDirection(1);
          setCurrentIndex(prev => prev + 1);
        }
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        if (currentIndex > 0) {
          setDirection(-1);
          setCurrentIndex(prev => prev - 1);
        }
      } else if (e.key === 'Home') {
        e.preventDefault();
        setDirection(-1);
        setCurrentIndex(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setDirection(1);
        setCurrentIndex(slides.length - 1);
      } else if (e.key === 'Escape') {
        setTocOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex]);
  const goTo = (idx: number) => {
    setDirection(idx > currentIndex ? 1 : -1);
    setCurrentIndex(idx);
  };
  const goPrev = () => currentIndex > 0 && goTo(currentIndex - 1);
  const goNext = () => currentIndex < slides.length - 1 && goTo(currentIndex + 1);
  const CurrentSlide = slides[currentIndex];
  return <div className="relative w-screen h-screen overflow-hidden bg-[var(--bg)]" data-id="element-1" data-mp="1jzv8e8l6zjoa">
      <AnimatePresence mode="wait" custom={direction} data-id="element-2" data-mp="1f83d5kr3cprt">
        <motion.div key={currentIndex} custom={direction} initial={{
        opacity: 0,
        x: direction > 0 ? 40 : -40
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: direction > 0 ? -40 : 40
      }} transition={{
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1]
      }} className="absolute inset-0" data-id="element-3" data-mp="1f6ijdxclpav0">
          <CurrentSlide index={currentIndex} total={slides.length} data-id="element-4" data-mp="2qs29lmeuc26c" />
        </motion.div>
      </AnimatePresence>

      {/* Top progress bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-black/5 z-40" data-id="element-5" data-mp="2gsbgnp5xhv8d">
        <motion.div className="h-full bg-[var(--brand-green)]" animate={{
        width: `${(currentIndex + 1) / slides.length * 100}%`
      }} transition={{
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1]
      }} data-id="element-6" data-mp="2lqcvtz08haek" />
      </div>

      {/* TOC toggle button */}
      <button onClick={() => setTocOpen(true)} aria-label="Open table of contents" className="absolute top-3 left-3 z-50 flex h-8 items-center gap-1.5 rounded-full border border-black/10 bg-white/90 px-3 text-[var(--brand-green)] shadow-sm backdrop-blur transition-all hover:bg-white" data-id="element-7" data-mp="24ic0ce9cllmi">
        <List className="h-3.5 w-3.5" data-id="element-8" data-mp="29eso37cmxxz2" />
        <span className="text-xs font-semibold" data-id="element-9" data-mp="2n7s214wrrk4i">Contents</span>
      </button>

      {/* Bottom nav cluster — compact, bottom-right, clear of content */}
      <div className="absolute bottom-3 right-3 z-50 flex items-center gap-2" data-id="element-10" data-mp="2j5ex2ipujq8s">
        <button onClick={goPrev} disabled={currentIndex === 0} aria-label="Previous slide" className="flex h-8 w-8 items-center justify-center rounded-full border border-black/10 bg-white/90 text-[var(--brand-green)] shadow-sm backdrop-blur transition-all hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-11" data-mp="2x67qt18bqhau">
          <ChevronLeft className="h-4 w-4" data-id="element-12" data-mp="za9fldph1j3x" />
        </button>

        <div className="flex items-baseline gap-1 rounded-full bg-[var(--brand-green)] px-3.5 py-1.5 shadow-md" data-id="element-13" data-mp="z9rarsoqtwke">
          <span className="text-white font-bold text-sm tabular-nums" data-id="element-14" data-mp="14b6t3czjdoz3">
            {currentIndex + 1}
          </span>
          <span className="text-[var(--brand-green-lighter)] text-xs font-medium" data-id="element-15" data-mp="1e79zww5c2bsd">
            / {slides.length}
          </span>
        </div>

        <button onClick={goNext} disabled={currentIndex === slides.length - 1} aria-label="Next slide" className="flex h-8 w-8 items-center justify-center rounded-full border border-black/10 bg-white/90 text-[var(--brand-green)] shadow-sm backdrop-blur transition-all hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-16" data-mp="gkue5i5virz0">
          <ChevronRight className="h-4 w-4" data-id="element-17" data-mp="3tz7ik2yczpb2" />
        </button>
      </div>

      {/* Collapsible Table of Contents */}
      <AnimatePresence data-id="element-18" data-mp="2qtmt9h9478o">
        {tocOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25
        }} onClick={() => setTocOpen(false)} className="absolute inset-0 z-[60] bg-black/40 backdrop-blur-sm" data-id="element-19" data-mp="2g2wfw5yj9qlg" />
            <motion.nav initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          duration: 0.35,
          ease: [0.4, 0, 0.2, 1]
        }} aria-label="Table of contents" className="absolute top-0 left-0 bottom-0 z-[70] w-[min(22rem,85vw)] bg-white shadow-2xl flex flex-col" data-id="element-20" data-mp="1x4tlfbn7v1g9">
              <div className="flex items-center justify-between px-6 py-5 border-b border-black/5" data-id="element-21" data-mp="19gadix82wqop">
                <div data-id="element-22" data-mp="zsok8j5jmprv">
                  <p className="text-[var(--brand-green)] text-xs font-semibold uppercase tracking-wider" data-id="element-23" data-mp="14p5616p9ss1s">
                    DERMX Snak Estimate
                  </p>
                  <h2 className="text-[var(--fg)] font-bold text-lg" data-id="element-24" data-mp="qrqcbprzhy40">
                    Contents
                  </h2>
                </div>
                <button onClick={() => setTocOpen(false)} aria-label="Close table of contents" className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-[var(--surface)] text-[var(--muted)]" data-id="element-25" data-mp="1igjrrlakb7cj">
                  <X className="h-5 w-5" data-id="element-26" data-mp="1dpv1g9amcgez" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto py-2" data-id="element-27" data-mp="21fpdwpr102yp">
                {slideTitles.map((s, idx) => {
              const active = idx === currentIndex;
              return <button key={idx} onClick={() => {
                goTo(idx);
                setTocOpen(false);
              }} className={`w-full flex items-center gap-4 px-6 py-3 text-left transition-colors ${active ? 'bg-[var(--surface)]' : 'hover:bg-[var(--surface)]/60'}`} data-id="element-28" data-mp="2688snq1g9453" data-mpi={idx}>
                      <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${active ? 'bg-[var(--brand-green)] text-white' : 'bg-[var(--surface)] text-[var(--muted)]'}`} data-id="element-29" data-mp="3vpvudr6s9f3k">
                        {idx + 1}
                      </span>
                      <span className="min-w-0" data-id="element-30" data-mp="3cwihd130ztc8">
                        <span className={`block text-[0.65rem] font-semibold uppercase tracking-wider ${active ? 'text-[var(--brand-green-light)]' : 'text-[var(--muted)]'}`} data-id="element-31" data-mp="3cvqnbup2u3o2">
                          {s.section}
                        </span>
                        <span className={`block text-sm font-semibold truncate ${active ? 'text-[var(--brand-green)]' : 'text-[var(--fg)]'}`} data-id="element-32" data-mp="2lrcwz8qwtuzo">
                          {s.title}
                        </span>
                      </span>
                    </button>;
            })}
              </div>
            </motion.nav>
          </>}
      </AnimatePresence>
    </div>;
}